import { ReactElement } from 'react';
import { useCurrentFrame } from 'remotion';

// Types
import { SnakeAnimationConfig, GridConfig, LinePath, Point } from '../types';

// Utils
import { createGridToPixel } from '../utils';

interface DecorativeLinesProps {
  lines: LinePath[];
  lineColor: string;
  grid: GridConfig;
  animation: Required<SnakeAnimationConfig>;
  width: number;
  height: number;
}

/**
 * Renders animated "snake" line segments over a grid-based path list.
 * Each line is staggered in time, converted from grid coordinates to pixels,
 * and drawn as a moving head/tail window that loops with a pause.
 *
 * @param props.lines - Grid-based polyline paths to render.
 * @param props.lineColor - Stroke color for rendered line segments.
 * @param props.grid - Grid configuration used for coordinate conversion.
 * @param props.animation - Animation timing and snake length configuration.
 * @param props.width - Pixel width of the drawing area.
 * @param props.height - Pixel height of the drawing area.
 */
export const DecorativeLines = ({
  lines,
  lineColor,
  grid,
  animation,
  width,
  height,
}: DecorativeLinesProps): ReactElement => {
  const frame = useCurrentFrame();
  const lineElements: ReactElement[] = [];
  const gridToPixel = createGridToPixel(grid, width, height);

  lines.forEach((line, lineIndex) => {
    // Offset each line in time to create a staggered reveal.
    const groupStartFrame = lineIndex * animation.staggerDelay;
    const adjustedFrame = Math.max(0, frame - groupStartFrame);

    const pathPoints: Point[] = [];
    for (let index = 0; index < line.points.length; index++) {
      const point = line.points[index];
      if (point) {
        // Convert grid coordinates to pixel coordinates for SVG drawing.
        pathPoints.push(gridToPixel(point));
      }
    }

    if (pathPoints.length < 2) return;

    const totalSegments = pathPoints.length - 1;
    const totalAnimationFrames = totalSegments * animation.framesPerSegment;

    // Loop progress across the whole path, then pause before restarting.
    const loopedFrame =
      adjustedFrame % (totalAnimationFrames + animation.loopPause);
    const progress = loopedFrame / animation.framesPerSegment;

    // Tail lags behind the head by snakeLength segments.
    const tailProgress = Math.max(0, progress - animation.snakeLength);

    for (let index = 0; index < totalSegments; index++) {
      const startPoint = pathPoints[index];
      const endPoint = pathPoints[index + 1];

      if (!startPoint || !endPoint) continue;

      // drawStart/drawEnd represent the fractional segment portion to render.
      let drawStart = 0;
      let drawEnd = 0;

      if (progress > index && progress <= index + 1) {
        drawEnd = progress - index;
      } else if (progress > index + 1) {
        drawEnd = 1;
      }

      if (tailProgress > index && tailProgress <= index + 1) {
        drawStart = tailProgress - index;
      } else if (tailProgress > index + 1) {
        drawStart = 1;
      }

      if (drawEnd > drawStart) {
        // Interpolate line endpoints within the segment based on progress.
        const x1 = startPoint.x + (endPoint.x - startPoint.x) * drawStart;
        const y1 = startPoint.y + (endPoint.y - startPoint.y) * drawStart;
        const x2 = startPoint.x + (endPoint.x - startPoint.x) * drawEnd;
        const y2 = startPoint.y + (endPoint.y - startPoint.y) * drawEnd;

        lineElements.push(
          <line
            key={`line-${lineIndex}-${index}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={lineColor}
            strokeWidth={1.5}
            strokeLinecap="round"
          />,
        );
      }
    }
  });

  return <>{lineElements}</>;
};
